import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Building2, Search, SquareUser, User, Users } from 'lucide-react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Input } from '@/common/components/ui/input';
import { cn } from '@/lib/utils';
import { supplyPointQueries } from '@/features/supply-points/queries';
import { organizationQueries } from '@/features/organizations/queries';
import { useSupplyPoint } from '@/features/supply-points/context/useSupplyPoint';
import type { OrgWithMembers } from '@/features/supply-points/services/schemas';
import { PeopleTab } from './PeopleTab';
import { OrganizationsTab } from './OrganizationsTab';

type AccessSubTab = 'people' | 'organizations';

export function AccessTab() {
  const { activeSupplyPoint } = useSupplyPoint();
  const supplyPointId = activeSupplyPoint!.id;

  const { data: accesses } = useSuspenseQuery(
    supplyPointQueries.accessBySupplyPoint(supplyPointId)
  );
  const { data: allOrgs } = useSuspenseQuery(organizationQueries.list());
  const { data: allMembers } = useSuspenseQuery(organizationQueries.membersList());

  const [subTab, setSubTab] = useState<AccessSubTab>('people');
  const [search, setSearch] = useState('');

  const orgAccesses: OrgWithMembers[] = activeSupplyPoint!.partners.map((partner) => {
    const membersVisible = allOrgs.some((org) => org.id === partner.id);
    return {
      id: partner.id,
      name: partner.name,
      membersVisible,
      members: membersVisible ? allMembers.filter((m) => m.partner === partner.id) : [],
    };
  });

  const orgMembers = orgAccesses.flatMap((o) => o.members);
  const totalPeople = new Set([
    ...accesses.map((a) => a.user.email),
    ...orgMembers.map((m) => m.user.email),
  ]).size;

  const stats = [
    {
      labelKey: 'direct',
      label: t`Direct members`,
      value: accesses.length,
      icon: User,
      colorText: 'text-blue-600',
      colorBg: 'bg-blue-50',
    },
    {
      labelKey: 'org',
      label: t`Via organizations`,
      value: orgMembers.length,
      icon: SquareUser,
      colorText: 'text-violet-600',
      colorBg: 'bg-violet-50',
    },
    {
      labelKey: 'orgs',
      label: t`Linked organizations`,
      value: orgAccesses.length,
      icon: Building2,
      colorText: 'text-emerald-600',
      colorBg: 'bg-emerald-50',
    },
  ];

  const tabs = [
    { id: 'people' as const, label: t`People`, icon: Users, count: totalPeople },
    {
      id: 'organizations' as const,
      label: t`Organizations`,
      icon: Building2,
      count: orgAccesses.length,
    },
  ];

  return (
    <div className="space-y-5 mt-6">
      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        {stats.map((stat) => (
          <div
            key={stat.labelKey}
            className="bg-card rounded-xl border border-border px-5 py-4 flex items-center gap-4"
          >
            <div className={cn('rounded-lg p-2.5', stat.colorBg)}>
              <stat.icon size={18} className={stat.colorText} />
            </div>
            <div>
              <div className={cn('text-2xl font-bold', stat.colorText)}>{stat.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tab panel */}
      <div className="rounded-2xl border border-border overflow-hidden bg-card">
        {/* Tab bar + search */}
        <div className="flex items-center justify-between px-4 pb-0 gap-4 border-b border-border">
          <div className="flex gap-1 self-end">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setSubTab(tab.id);
                  setSearch('');
                }}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors',
                  subTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                )}
              >
                <tab.icon size={15} />
                {tab.label}
                <span
                  className={cn(
                    'ml-1 px-1.5 py-0.5 rounded-md text-[11px] font-semibold',
                    subTab === tab.id
                      ? 'bg-primary/10 text-primary'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          <div className="relative my-3 self-center">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              placeholder={t`Search...`}
              className="pl-8 h-8 w-52 text-xs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {subTab === 'people' && (
          <PeopleTab
            supplyPointId={supplyPointId}
            accesses={accesses}
            orgAccesses={orgAccesses}
            search={search}
            totalPeople={totalPeople}
          />
        )}

        {subTab === 'organizations' && (
          <OrganizationsTab
            supplyPointId={supplyPointId}
            orgAccesses={orgAccesses}
            search={search}
          />
        )}
      </div>
    </div>
  );
}
